// ============================================================================
// PITCHES TAB — pitchTypes.jsx
// ============================================================================
// Comms → Pitches. Three views:
//   Pitch types — the rules, sections and chapters a deck of each kind starts
//                 from (docs/pitch-deck-generator-plan.md, sections 3.1 and 7.2).
//   Decks       — every deck made so far, and the form that drafts a new one
//                 from an inquiry through the selection engine.
//   A deck      — the builder (pitchDeckBuilder.jsx), opened from the list.
//
// Every edit writes straight to comms.pitch_types or comms.pitch_decks through
// commsUpdate and bumps the tab's data revision, the same pattern as the rest
// of CommsPage.
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { DndContext, closestCenter, SortableContext, horizontalListSortingStrategy, arrayMove } from './pitchDnd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Trash2, ChevronDown, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';
import { commsInsert, commsUpdate, commsDelete, commsUpsertSetting } from './commsUtils';
import { S, GOLD, Chip, FilterBtn, BlockTitle, NoteArea, InlineInput, AddLine, BtnGhost, BtnPrimary, EmptyNote, statusClass } from './commsUi';
import { MONO, fieldLabel, selectStyle, iconBtn, ToggleChip, NumberField, useDragSensors, SectionsEditor, categoriesFrom } from './pitchUi';
import { PitchDeckBuilder } from './pitchDeckBuilder';
import {
  FEE_BANDS, CLIENT_TYPES, FORMATS, QUALITY_TIERS, DEFAULT_SECTIONS, DEFAULT_RULES, ATTACHMENT_MAX_BYTES,
  newChapter, readSections, readChapters, readRules,
} from '../../pitch/pitchSections';
import { inquiryProfile, rankPitchTypes, buildDraftDeck, DEFAULT_EUR_INR } from '../../pitch/selectPitchContent';

// ── Chapters ─────────────────────────────────────────────────────────────────

function SortableCodeChip({ code, name, confidential, canEdit, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: code, disabled: !canEdit });
  return (
    <span ref={setNodeRef} title={name || code} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: MONO, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#F1EFE9', border: `1px solid ${confidential ? '#4A3C30' : 'rgba(27,27,25,.14)'}`, borderRadius: 2, padding: '3px 7px' }}>
      <span {...attributes} {...listeners} style={{ cursor: canEdit ? 'grab' : 'default', color: confidential ? '#6A5E50' : GOLD, touchAction: 'none' }}>{code}</span>
      {confidential && <span style={{ fontSize: 9, color: '#6A5E50' }} title="Confidential project">NDA</span>}
      {canEdit && <button onClick={onRemove} title="Remove from chapter" style={{ ...iconBtn, padding: 0, fontSize: 12, lineHeight: 1 }}>×</button>}
    </span>
  );
}

function ProjectOrderEditor({ codes, category, projects, canEdit, onChange }) {
  const sensors = useDragSensors();
  const byCode = useMemo(() => Object.fromEntries((projects || []).map(p => [p.project_code, p])), [projects]);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onChange(arrayMove(codes, codes.indexOf(active.id), codes.indexOf(over.id)));
  };
  const cat = (category || '').toLowerCase();
  const remaining = (projects || []).filter(p => p.project_code && !codes.includes(p.project_code));
  const inCategory = remaining.filter(p => (p.typology || '').toLowerCase() === cat);
  const others = remaining.filter(p => (p.typology || '').toLowerCase() !== cat);
  const optionLabel = p => `${p.project_code} · ${p.name}${p.confidential ? ' (NDA)' : ''}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={codes} strategy={horizontalListSortingStrategy}>
          {codes.map(code => (
            <SortableCodeChip key={code} code={code} name={byCode[code]?.name} confidential={!!byCode[code]?.confidential} canEdit={canEdit} onRemove={() => onChange(codes.filter(c => c !== code))} />
          ))}
        </SortableContext>
      </DndContext>
      {codes.length === 0 && <span style={S.meta}>No default order. The engine fills this chapter by score.</span>}
      {canEdit && remaining.length > 0 && (
        <select value="" onChange={e => { if (e.target.value) onChange([...codes, e.target.value]); }} style={{ ...selectStyle, minWidth: 120, padding: '4px 8px', fontSize: 12 }} title="Add a project to the default order">
          <option value="">+ project</option>
          {inCategory.length > 0 && <optgroup label={category ? `In ${category}` : 'Projects'}>{inCategory.map(p => <option key={p.project_code} value={p.project_code}>{optionLabel(p)}</option>)}</optgroup>}
          {others.length > 0 && <optgroup label="Other categories">{others.map(p => <option key={p.project_code} value={p.project_code}>{optionLabel(p)}</option>)}</optgroup>}
        </select>
      )}
    </div>
  );
}

function ChaptersEditor({ chapters, categories, projects, canEdit, onChange }) {
  const patch = (id, fields) => onChange(chapters.map(c => c.id === id ? { ...c, ...fields } : c));
  const remove = id => onChange(chapters.filter(c => c.id !== id));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= chapters.length) return;
    onChange(arrayMove(chapters, idx, j));
  };
  const labelFor = key => (categories.find(c => c.key === key) || {}).label || key;

  return (
    <div>
      {chapters.map((ch, idx) => (
        <div key={ch.id} style={{ padding: '10px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ ...S.eyebrow, fontSize: 10, minWidth: 20 }}>{idx + 1}</span>
            <select value={ch.category} disabled={!canEdit} onChange={e => patch(ch.id, { category: e.target.value, label: ch.label || labelFor(e.target.value) })} style={selectStyle}>
              <option value="">Category…</option>
              {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            {canEdit
              ? <InlineInput value={ch.label} onBlurSave={v => patch(ch.id, { label: v })} placeholder="Chapter title on the divider page…" style={{ flex: 1, minWidth: 200 }} />
              : <span style={{ flex: 1, fontSize: 14 }}>{ch.label || labelFor(ch.category)}</span>}
            {canEdit && (
              <span style={{ display: 'inline-flex', gap: 2 }}>
                <button onClick={() => move(idx, -1)} title="Move up" style={iconBtn} disabled={idx === 0}><ArrowUp size={13} /></button>
                <button onClick={() => move(idx, 1)} title="Move down" style={iconBtn} disabled={idx === chapters.length - 1}><ArrowDown size={13} /></button>
                <button onClick={() => remove(ch.id)} title="Remove chapter" style={iconBtn}><Trash2 size={13} /></button>
              </span>
            )}
          </div>
          <div style={{ paddingLeft: 30 }}>
            <ProjectOrderEditor codes={ch.project_order} category={ch.category} projects={projects} canEdit={canEdit} onChange={codes => patch(ch.id, { project_order: codes })} />
          </div>
        </div>
      ))}
      {chapters.length === 0 && <EmptyNote text="No chapters. Decks of this type get one flat run of projects, ordered by score." />}
      {canEdit && (
        <AddLine>
          <select value="" onChange={e => { if (e.target.value) onChange([...chapters, newChapter(e.target.value, labelFor(e.target.value))]); }} style={selectStyle}>
            <option value="">Add a chapter…</option>
            {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </AddLine>
      )}
    </div>
  );
}

// ── One pitch type ───────────────────────────────────────────────────────────

function ruleSummary(rules, categories) {
  const parts = [];
  if (rules.categories.length) parts.push(rules.categories.map(k => (categories.find(c => c.key === k) || {}).label || k).join(', '));
  if (rules.fee_bands.length) parts.push(rules.fee_bands.map(k => (FEE_BANDS.find(b => b.key === k) || {}).label || k).join(', '));
  if (rules.client_types.length) parts.push(rules.client_types.map(k => (CLIENT_TYPES.find(c => c.key === k) || {}).label || k).join(', '));
  if (rules.plot_min != null || rules.plot_max != null) parts.push(`plot ${rules.plot_min ?? '0'} to ${rules.plot_max ?? '∞'} m²`);
  return parts.length ? parts.join(' · ') : 'Matches any inquiry';
}

function PitchTypeCard({ type, open, onToggle, categories, themes, projects, canEdit, isFirst, isLast, onMove, refresh, showToast }) {
  const rules = readRules(type.rules);
  const sections = readSections(type.sections);
  const chapters = readChapters(type.chapters);

  const save = async fields => {
    try { await commsUpdate('pitch_types', 'comms_id', type.comms_id, { ...fields, updated_at: new Date().toISOString() }); refresh(); }
    catch (e) { showToast(e.message, 'error'); }
  };
  const saveRules = fields => save({ rules: { ...rules, ...fields } });
  const toggleIn = (list, key) => list.includes(key) ? list.filter(k => k !== key) : [...list, key];
  const del = async () => {
    if (!window.confirm(`Delete the pitch type "${type.label}"? Decks already made from it keep their content.`)) return;
    try { await commsDelete('pitch_types', 'comms_id', type.comms_id); refresh(); showToast('Pitch type deleted'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  return (
    <div style={{ ...S.card, opacity: type.active ? 1 : 0.7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={onToggle} style={{ ...iconBtn, color: '#21302A' }} title={open ? 'Collapse' : 'Expand'}>{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
        <div style={{ flex: 1, minWidth: 220 }}>
          {canEdit
            ? <InlineInput value={type.label} onBlurSave={v => { if (v.trim()) save({ label: v.trim() }); }} placeholder="Pitch type name…" style={{ fontSize: 17, fontFamily: "'Spectral', Georgia, serif", border: 'none', background: 'transparent', padding: '2px 0' }} />
            : <h3 style={S.h3}>{type.label}</h3>}
          <p style={S.meta}>{ruleSummary(rules, categories)} · {chapters.length} chapter{chapters.length === 1 ? '' : 's'} · {sections.filter(s => s.enabled).length} sections</p>
        </div>
        <Chip label={type.active ? 'Active' : 'Inactive'} cls={type.active ? 'pub' : 'new'} onClick={canEdit ? () => save({ active: !type.active }) : undefined} />
        {canEdit && (
          <span style={{ display: 'inline-flex', gap: 2 }}>
            <button onClick={() => onMove(-1)} disabled={isFirst} title="Move up" style={iconBtn}><ArrowUp size={13} /></button>
            <button onClick={() => onMove(1)} disabled={isLast} title="Move down" style={iconBtn}><ArrowDown size={13} /></button>
            <button onClick={del} title="Delete pitch type" style={iconBtn}><Trash2 size={13} /></button>
          </span>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 18, display: 'grid', gap: 26 }}>
          <div>
            <span style={fieldLabel}>Description</span>
            <NoteArea value={type.description || ''} onChange={v => save({ description: v })} placeholder="Who this pitch is for and what it leads with…" minHeight={48} saveOnBlur />
          </div>

          <div>
            <BlockTitle left="Matches inquiries with" />
            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <span style={fieldLabel}>Project category</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {categories.map(c => <ToggleChip key={c.key} label={c.label} active={rules.categories.includes(c.key)} disabled={!canEdit} onClick={() => saveRules({ categories: toggleIn(rules.categories, c.key) })} />)}
                </div>
              </div>
              <div>
                <span style={fieldLabel}>Estimated fee band</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {FEE_BANDS.map(b => <ToggleChip key={b.key} label={b.label} active={rules.fee_bands.includes(b.key)} disabled={!canEdit} onClick={() => saveRules({ fee_bands: toggleIn(rules.fee_bands, b.key) })} />)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
                <div>
                  <span style={fieldLabel}>Client type</span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {CLIENT_TYPES.map(c => <ToggleChip key={c.key} label={c.label} active={rules.client_types.includes(c.key)} disabled={!canEdit} onClick={() => saveRules({ client_types: toggleIn(rules.client_types, c.key) })} />)}
                  </div>
                </div>
                <div>
                  <span style={fieldLabel}>Plot area, m² (fallback when no fee is estimated)</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <NumberField value={rules.plot_min} onSave={v => saveRules({ plot_min: v })} placeholder="min" width={90} />
                    <span style={S.meta}>to</span>
                    <NumberField value={rules.plot_max} onSave={v => saveRules({ plot_max: v })} placeholder="max" width={90} />
                  </div>
                </div>
              </div>
              <p style={S.body}>Leave a rule empty to match everything. The engine picks the type with the most matching rules and the reviewer can always switch.</p>
            </div>
          </div>

          <div>
            <BlockTitle left="Deck defaults" />
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 14 }}>
              <div>
                <span style={fieldLabel}>Projects per chapter</span>
                <NumberField value={type.projects_per_chapter} min={1} onSave={v => save({ projects_per_chapter: v || 1 })} />
              </div>
              <div>
                <span style={fieldLabel}>Supporting images per project</span>
                <NumberField value={type.images_per_project} min={0} onSave={v => save({ images_per_project: v == null ? 0 : v })} />
              </div>
              <div>
                <span style={fieldLabel}>Format</span>
                <select value={type.format || 'screen'} disabled={!canEdit} onChange={e => save({ format: e.target.value })} style={selectStyle}>
                  {FORMATS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </div>
            </div>
            <span style={fieldLabel}>Base introduction (used when no rewrite is requested)</span>
            <NoteArea value={type.intro_template || ''} onChange={v => save({ intro_template: v })} placeholder="Dear …, thank you for … Architecture BRIO is …" minHeight={110} saveOnBlur />
          </div>

          <div>
            <BlockTitle left="Sections, in order" />
            <SectionsEditor sections={sections} themes={themes} canEdit={canEdit} onChange={next => save({ sections: next })} />
          </div>

          <div>
            <BlockTitle left="Chapters and default project order" />
            <p style={{ ...S.body, marginBottom: 10 }}>Projects listed here come first, in this order. Remaining slots are filled by score from the chapter's category. A deck copies this order and can change it without touching the type.</p>
            <ChaptersEditor chapters={chapters} categories={categories} projects={projects} canEdit={canEdit} onChange={next => save({ chapters: next })} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Decks ────────────────────────────────────────────────────────────────────

function formatBytes(n) {
  if (n == null) return '';
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function clientFor(inquiry, clients) {
  return inquiry ? (clients || []).find(c => Number(c.id) === Number(inquiry.client_id)) || null : null;
}

function inquiryLabel(inq, clients) {
  const c = clientFor(inq, clients);
  const who = inq.client_org_name_override || inq.client_name_override || (c && (c.display_name || c.name)) || '';
  return [inq.project_name || 'Untitled inquiry', who, inq.project_type].filter(Boolean).join(' · ');
}

// Draft a deck from an inquiry (or none) through the selection engine.
function NewDeckForm({ data, types, showToast, onCreated, forInquiry }) {
  const inquiries = data.inquiries || [];
  const clients = data.clients || [];
  const rate = (data.fx_rates && data.fx_rates[0] && Number(data.fx_rates[0].avg_rate)) || DEFAULT_EUR_INR;
  const [inquiryId, setInquiryId] = useState(forInquiry || '');
  const [typeId, setTypeId] = useState('');
  const [allowConfidential, setAllowConfidential] = useState(false);
  const [busy, setBusy] = useState(false);
  const inquiry = inquiries.find(i => i.id === inquiryId) || null;
  const client = clientFor(inquiry, clients);
  const profile = useMemo(() => inquiry ? inquiryProfile(inquiry, client, rate) : null, [inquiry, client, rate]);
  const ranked = useMemo(
    () => profile ? rankPitchTypes(types, profile) : types.filter(t => t.active !== false).map(t => ({ type: t, score: 0, reasons: [] })),
    [profile, types]
  );
  useEffect(() => {
    if (ranked.length && (!typeId || !ranked.some(r => r.type.comms_id === typeId))) setTypeId(ranked[0].type.comms_id);
  }, [ranked]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = ranked.find(r => r.type.comms_id === typeId) || null;
  const feeBand = profile && profile.feeBand ? (FEE_BANDS.find(b => b.key === profile.feeBand) || {}).label : null;

  const create = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const draft = buildDraftDeck({
        inquiry, client, pitchType: chosen.type, projects: data.projects || [], ranks: data.asset_ranks || [], tags: data.asset_tags || [],
        awards: data.awards || [], publications: data.publications || [], themes: data.themes || [], themeAssets: data.theme_assets || [],
        allowConfidential, eurInrRate: rate,
      });
      const row = {
        comms_id: 'deck-' + Date.now().toString(36), inquiry_id: inquiry ? inquiry.id : null, pitch_type_id: chosen.type.comms_id,
        title: draft.title, client_name: draft.client_name, format: draft.format, quality: draft.quality, status: 'draft',
        sections: draft.sections, chapters: draft.chapters, intro_text: draft.intro_text,
        // Kept on the deck so "Suggest again" and hand added projects honour it
        // too; it used to live only in this form's state.
        allow_confidential: draft.allow_confidential,
      };
      await commsInsert('pitch_decks', row);
      showToast(draft.reasons.length ? `Deck drafted. ${draft.reasons.join('. ')}` : 'Deck drafted');
      onCreated(row.comms_id);
    } catch (e) { showToast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ ...S.card, marginBottom: 20 }}>
      <div style={{ ...S.eyebrow, marginBottom: 10 }}>New deck</div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 2, minWidth: 260 }}>
          <span style={fieldLabel}>Inquiry</span>
          <select value={inquiryId} onChange={e => setInquiryId(e.target.value)} style={{ ...selectStyle, width: '100%' }}>
            <option value="">No inquiry (general profile)</option>
            {inquiries.map(i => <option key={i.id} value={i.id}>{inquiryLabel(i, clients)}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <span style={fieldLabel}>Pitch type{profile ? ', best match first' : ''}</span>
          <select value={typeId} onChange={e => setTypeId(e.target.value)} style={{ ...selectStyle, width: '100%' }}>
            {ranked.map(r => <option key={r.type.comms_id} value={r.type.comms_id}>{r.type.label}{profile ? ` (${r.score >= 0 ? '+' : ''}${r.score})` : ''}</option>)}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1B1B19', cursor: 'pointer', paddingBottom: 8 }}>
          <input type="checkbox" checked={allowConfidential} onChange={e => setAllowConfidential(e.target.checked)} style={{ accentColor: GOLD }} />
          Allow confidential projects
        </label>
        <BtnPrimary onClick={busy ? undefined : create}>{busy ? 'Drafting…' : 'Draft deck'}</BtnPrimary>
      </div>
      {profile && (
        <p style={{ ...S.meta, marginTop: 10 }}>
          Reads as {[profile.category ? `${profile.category}` : 'an unmapped category', feeBand, profile.clientType === 'family' ? 'private client' : profile.clientType === 'org' ? 'organisation' : 'client type unknown', profile.city].filter(Boolean).join(' · ')}.
          {chosen && chosen.reasons.length ? ` ${chosen.type.label}: ${chosen.reasons.join(', ')}.` : ''}
        </p>
      )}
      {!profile && <p style={{ ...S.meta, marginTop: 10 }}>Without an inquiry the engine fills the chapters by score alone. Only active inquiries are listed.</p>}
    </div>
  );
}

function DecksView({ data, types, canEdit, refresh, showToast, onOpen, onCreated, forInquiry }) {
  const decks = data.pitch_decks || [];
  const typeLabel = id => (types.find(t => t.comms_id === id) || {}).label || '';
  const cell = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid rgba(27,27,25,.08)', verticalAlign: 'top' };
  const head = { ...cell, ...S.eyebrow, fontSize: 10, borderBottom: '1px solid #33473F' };
  return (
    <div>
      {canEdit && (
        <div style={{ ...S.card, marginBottom: 20 }}>
          <div style={{ ...S.eyebrow, marginBottom: 6 }}>Closing page</div>
          <p style={{ ...S.body, marginBottom: 8 }}>Contact block printed on the last page of every deck. One paragraph per studio, a blank line between.</p>
          <NoteArea
            value={typeof data.practice?.pitch_contact === 'string' ? data.practice.pitch_contact : ''}
            onChange={async v => { try { await commsUpsertSetting('practice_settings', 'pitch_contact', v); refresh(); } catch (e) { showToast(e.message, 'error'); } }}
            placeholder={'Architecture BRIO Rotterdam\nStreet, postcode, city\nemail · phone\n\nArchitecture BRIO Mumbai\n…'}
            minHeight={110} saveOnBlur
          />
        </div>
      )}
      {canEdit && <NewDeckForm data={data} types={types} showToast={showToast} onCreated={onCreated} forInquiry={forInquiry} />}
      {decks.length === 0
        ? <EmptyNote text="No decks yet. Draft one above." />
        : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
              <thead><tr>
                <th style={head} align="left">Deck</th><th style={head} align="left">Client</th><th style={head} align="left">Type</th>
                <th style={head} align="left">Status</th><th style={head} align="left">Quality</th><th style={head} align="left">Rendered</th><th style={head} align="right">Size</th>
              </tr></thead>
              <tbody>
                {decks.map(d => (
                  <tr key={d.comms_id} onClick={() => onOpen(d.comms_id)} style={{ cursor: 'pointer' }} title="Open deck">
                    <td style={{ ...cell, fontWeight: 600 }}>{d.title}</td>
                    <td style={cell}>{d.client_name || ''}</td>
                    <td style={cell}>{typeLabel(d.pitch_type_id)}</td>
                    <td style={cell}><Chip label={d.status || 'draft'} cls={statusClass(d.status === 'sent' ? 'done' : d.status)} /></td>
                    <td style={cell}>{(QUALITY_TIERS.find(q => q.key === d.quality) || {}).label || d.quality}</td>
                    <td style={cell}>{d.rendered_at ? new Date(d.rendered_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</td>
                    <td style={{ ...cell, textAlign: 'right', fontFamily: MONO, fontSize: 12 }} title={d.pdf_bytes != null && d.pdf_bytes < ATTACHMENT_MAX_BYTES ? 'Small enough to attach' : ''}>{formatBytes(d.pdf_bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// ── Tab ──────────────────────────────────────────────────────────────────────

export function PitchesTab({ data, canEdit, refresh, showToast, openForInquiry }) {
  // Arriving from an inquiry's drawer: open the decks list rather than the
  // pitch types, and if that inquiry already has a deck, open it.
  const [sub, setSub] = useState(openForInquiry ? 'decks' : 'types');
  const [openId, setOpenId] = useState(null);
  const [openDeckId, setOpenDeckId] = useState(() => {
    if (!openForInquiry) return null;
    const mine = (data.pitch_decks || []).filter(d => d.inquiry_id === openForInquiry);
    return mine.length === 1 ? mine[0].comms_id : null;
  });
  const [newLabel, setNewLabel] = useState('');
  const types = useMemo(() => [...(data.pitch_types || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)), [data.pitch_types]);
  const decks = data.pitch_decks || [];
  const themes = data.themes || [];
  const projects = useMemo(() => data.projects || [], [data.projects]);
  const categories = useMemo(() => categoriesFrom(data.project_categories, projects), [data.project_categories, projects]);

  const addType = async () => {
    const label = newLabel.trim();
    if (!label) return;
    const id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') + '-' + Date.now().toString(36);
    try {
      await commsInsert('pitch_types', { comms_id: id, label, rules: DEFAULT_RULES, sections: DEFAULT_SECTIONS, chapters: [], sort_order: types.length + 1 });
      setNewLabel(''); setOpenId(id); refresh(); showToast('Pitch type added');
    } catch (e) { showToast(e.message, 'error'); }
  };

  // Swap sort_order with the neighbour; both rows are written so the order is
  // stable even where two types shared a value.
  const moveType = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= types.length) return;
    const reordered = arrayMove(types, idx, j);
    try {
      await Promise.all(reordered.map((t, i) => (t.sort_order === i + 1 ? null : commsUpdate('pitch_types', 'comms_id', t.comms_id, { sort_order: i + 1 }))).filter(Boolean));
      refresh();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const openDeck = openDeckId ? decks.find(d => d.comms_id === openDeckId) : null;
  if (openDeckId) {
    if (!openDeck) return <div style={{ padding: 40, color: '#9A988F', fontSize: 13 }}>Loading deck…</div>;
    return <PitchDeckBuilder deck={openDeck} data={data} canEdit={canEdit} refresh={refresh} showToast={showToast} onBack={() => setOpenDeckId(null)} />;
  }

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ ...S.eyebrow, marginBottom: 6 }}>Pitch decks</div>
        <h1 style={S.h1}>Pitches</h1>
        <p style={{ ...S.body, marginTop: 10, maxWidth: 600 }}>Pitch types describe what a deck for a kind of inquiry contains: which sections, which chapters, which projects first. A deck starts from the type the inquiry matches and is adjusted before it is rendered.</p>
      </div>
      <div style={S.filterRow}>
        <FilterBtn label="Pitch types" active={sub === 'types'} onClick={() => setSub('types')} />
        <FilterBtn label={`Decks${decks.length ? ` (${decks.length})` : ''}`} active={sub === 'decks'} onClick={() => setSub('decks')} />
      </div>

      {sub === 'types' && (
        <div>
          {types.map((t, idx) => (
            <PitchTypeCard
              key={t.comms_id} type={t} open={openId === t.comms_id} onToggle={() => setOpenId(openId === t.comms_id ? null : t.comms_id)}
              categories={categories} themes={themes} projects={projects} canEdit={canEdit}
              isFirst={idx === 0} isLast={idx === types.length - 1} onMove={dir => moveType(idx, dir)}
              refresh={refresh} showToast={showToast}
            />
          ))}
          {types.length === 0 && <EmptyNote text="No pitch types yet. Add one below." />}
          {canEdit && (
            <AddLine>
              <InlineInput value={newLabel} onChange={setNewLabel} placeholder="New pitch type, e.g. Boutique hospitality…" style={{ maxWidth: 320 }} />
              <BtnGhost onClick={addType}>+ Add pitch type</BtnGhost>
            </AddLine>
          )}
        </div>
      )}

      {sub === 'decks' && (
        <DecksView data={data} types={types} canEdit={canEdit} refresh={refresh} showToast={showToast} onOpen={setOpenDeckId} onCreated={id => { refresh(); setOpenDeckId(id); }} forInquiry={openForInquiry} />
      )}
    </div>
  );
}
